import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import reklamacjeService from '../../../services/reklamacjeService';
import EmailConfigModal from '../../reklamacje/EmailConfigModal';
import { FaEnvelope, FaSyncAlt, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

/**
 * Konfiguracja kont e-mail ODDZIALOW dla wysylki reklamacji do dostawcy.
 * Kazdy oddzial ma wlasne konto (location_email_config, unikat na location_id),
 * wiec tu widac wszystkie lokalizacje naraz wraz ze statusem konfiguracji.
 *
 * Dostep: tylko admin (endpointy /reklamacje/email-config sa @require_admin).
 *
 * UI standard V3SYS: font 12px/11px/10px, padding 6px 12px / 4px 10px,
 * wylacznie tokeny kolorow var(--...).
 */
/**
 * Slownik adresatow reklamacji. Pracownik przy wysylce zaznacza pozycje z tej
 * listy (moze tez dopisac adres spoza niej), wiec tutaj admin decyduje,
 * co pracownik zobaczy.
 */
const AdresaciSekcja = () => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowy, setNowy] = useState({ nazwa: '', email: '', opis: '', domyslny: false });
  const [zapisywanie, setZapisywanie] = useState(false);

  const wczytaj = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reklamacjeService.getAdresaci(true);
      const dane = res?.data || res || [];
      setLista(Array.isArray(dane) ? dane : []);
    } catch (e) {
      toast.error('Nie udalo sie pobrac listy adresatow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { wczytaj(); }, [wczytaj]);

  const dodaj = async () => {
    if (!nowy.nazwa.trim() || !nowy.email.trim()) {
      toast.error('Podaj nazwe i adres e-mail');
      return;
    }
    setZapisywanie(true);
    try {
      await reklamacjeService.addAdresat(nowy);
      toast.success('Dodano adresata');
      setNowy({ nazwa: '', email: '', opis: '', domyslny: false });
      wczytaj();
    } catch (e) {
      toast.error(e?.message || 'Blad dodawania adresata');
    } finally {
      setZapisywanie(false);
    }
  };

  const przelacz = async (a, pole) => {
    try {
      await reklamacjeService.updateAdresat(a.id, { ...a, [pole]: !a[pole] });
      wczytaj();
    } catch (e) {
      toast.error(e?.message || 'Blad zapisu');
    }
  };

  const usun = async (a) => {
    if (!window.confirm(`Usunac adresata "${a.nazwa}" (${a.email})?`)) return;
    try {
      await reklamacjeService.deleteAdresat(a.id);
      toast.success('Usunieto adresata');
      wczytaj();
    } catch (e) {
      toast.error(e?.message || 'Blad usuwania');
    }
  };

  const pole = {
    padding: '6px 10px', fontSize: '12px', borderRadius: '4px',
    border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
  };
  const kom = { padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--border)', fontSize: '12px' };

  return (
    <div style={{ marginTop: '1.25rem' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 2px 0' }}>
        {'📮'} Adresaci reklamacji
      </h3>
      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 0.6rem 0' }}>
        Adresy z tej listy pracownik zaznacza przy wysylce reklamacji.
        Zaznaczone jako <strong>domyslne</strong> beda wybrane od razu.
        Adres spoza listy pracownik moze wpisac recznie.
      </p>

      {/* Dodawanie */}
      <div style={{
        display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center',
        padding: '0.6rem', backgroundColor: 'var(--bg)', borderRadius: '4px', marginBottom: '0.6rem'
      }}>
        <input style={{ ...pole, flex: '1 1 160px' }} placeholder="Nazwa (np. Hurtownia X)"
          value={nowy.nazwa} onChange={(e) => setNowy(p => ({ ...p, nazwa: e.target.value }))} />
        <input style={{ ...pole, flex: '1 1 200px' }} placeholder="reklamacje@dostawca.pl"
          value={nowy.email} onChange={(e) => setNowy(p => ({ ...p, email: e.target.value }))} />
        <input style={{ ...pole, flex: '1 1 160px' }} placeholder="Opis (opcjonalnie)"
          value={nowy.opis} onChange={(e) => setNowy(p => ({ ...p, opis: e.target.value }))} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer' }}>
          <input type="checkbox" checked={nowy.domyslny}
            onChange={(e) => setNowy(p => ({ ...p, domyslny: e.target.checked }))} />
          domyslny
        </label>
        <button onClick={dodaj} disabled={zapisywanie} style={{
          padding: '6px 12px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
          borderRadius: '4px', border: '1px solid var(--success)',
          backgroundColor: 'var(--card-bg)', color: 'var(--success)'
        }}>{zapisywanie ? 'Dodaje...' : '+ Dodaj'}</button>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '0.5rem' }}>Ladowanie...</div>
      ) : lista.length === 0 ? (
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '0.75rem', textAlign: 'center' }}>
          Lista jest pusta {'—'} pracownik bedzie musial wpisywac adres recznie.
        </div>
      ) : (
        <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  {['NAZWA', 'E-MAIL', 'OPIS', 'DOMYSLNY', 'AKTYWNY', ''].map((h, i) => (
                    <th key={i} style={{ ...kom, textAlign: i === 5 ? 'right' : 'left', fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map(a => (
                  <tr key={a.id} style={{ opacity: a.aktywny ? 1 : 0.55 }}>
                    <td style={{ ...kom, fontWeight: 600 }}>{a.nazwa}</td>
                    <td style={kom}>{a.email}</td>
                    <td style={{ ...kom, color: 'var(--text-muted)', fontSize: '11px' }}>{a.opis || '—'}</td>
                    <td style={kom}>
                      <input type="checkbox" checked={!!a.domyslny} onChange={() => przelacz(a, 'domyslny')}
                        style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={kom}>
                      <input type="checkbox" checked={!!a.aktywny} onChange={() => przelacz(a, 'aktywny')}
                        style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...kom, textAlign: 'right' }}>
                      <button onClick={() => usun(a)} style={{
                        padding: '4px 10px', fontSize: '11px', cursor: 'pointer', borderRadius: '4px',
                        border: '1px solid var(--danger)', backgroundColor: 'var(--card-bg)', color: 'var(--danger)'
                      }}>Usun</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ReklamacjeEmailTab = () => {
  const [locations, setLocations] = useState([]);
  const [configs, setConfigs] = useState({});   // location_id -> konfiguracja lub null
  const [loading, setLoading] = useState(true);
  const [modalFor, setModalFor] = useState(null); // { id, nazwa }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/locations/');
      const lista = Array.isArray(data) ? data : (data?.data || []);
      setLocations(lista);

      // Konfiguracje pobieramy rownolegle; brak konfiguracji nie jest bledem.
      const wyniki = await Promise.all(lista.map(async (loc) => {
        try {
          const res = await reklamacjeService.getEmailConfig(loc.id);
          return [loc.id, res?.data ?? res ?? null];
        } catch (e) {
          return [loc.id, null];
        }
      }));
      const mapa = {};
      wyniki.forEach(([id, cfg]) => { mapa[id] = cfg; });
      setConfigs(mapa);
    } catch (err) {
      toast.error('Nie udalo sie pobrac lokalizacji');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const skonfigurowane = locations.filter(l => {
    const c = configs[l.id];
    return c && c.from_email;
  }).length;

  const komorka = { padding: '0.5rem 0.6rem', borderBottom: '1px solid var(--border)', fontSize: '12px' };

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Naglowek */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '8px', marginBottom: '0.75rem'
      }}>
        <div>
          <h3 style={{
            fontSize: '14px', fontWeight: 600, margin: '0 0 2px 0',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <FaEnvelope style={{ color: 'var(--info)', fontSize: '13px' }} />
            Konta e-mail oddzialow (reklamacje)
          </h3>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
            Z tego adresu wychodzi reklamacja do dostawcy. Kazdy oddzial ma wlasne konto.
            Skonfigurowane: {skonfigurowane} z {locations.length}
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          style={{
            padding: '6px 12px', fontSize: '11px', fontWeight: '500', cursor: 'pointer',
            border: '1px solid var(--border)', borderRadius: '4px',
            backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)'
          }}
        >
          <FaSyncAlt style={{ fontSize: '10px', marginRight: '4px' }} />
          {loading ? 'Laduje...' : 'Odswiez'}
        </button>
      </div>

      {loading && locations.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Ladowanie lokalizacji...
        </div>
      ) : locations.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak lokalizacji w systemie.
        </div>
      ) : (
        <div style={{
          backgroundColor: 'var(--card-bg)', borderRadius: '4px', overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ ...komorka, textAlign: 'left', fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)' }}>ODDZIAL</th>
                  <th style={{ ...komorka, textAlign: 'left', fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)' }}>ADRES NADAWCY</th>
                  <th style={{ ...komorka, textAlign: 'left', fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)' }}>SPOSOB WYSYLKI</th>
                  <th style={{ ...komorka, textAlign: 'left', fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)' }}>STATUS</th>
                  <th style={{ ...komorka, textAlign: 'right', fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)' }}>AKCJA</th>
                </tr>
              </thead>
              <tbody>
                {locations.map(loc => {
                  const cfg = configs[loc.id];
                  const maAdres = !!(cfg && cfg.from_email);
                  const aktywny = !!(cfg && cfg.aktywny);
                  const nazwa = loc.nazwa || loc.name || `Lokalizacja ${loc.id}`;

                  let statusTlo = 'var(--danger-light)';
                  let statusKolor = 'var(--danger)';
                  let statusTekst = 'Brak konfiguracji';
                  let StatusIkona = FaExclamationTriangle;

                  if (maAdres && aktywny) {
                    statusTlo = 'var(--success-light)';
                    statusKolor = 'var(--success)';
                    statusTekst = 'Gotowe do wysylki';
                    StatusIkona = FaCheckCircle;
                  } else if (maAdres && !aktywny) {
                    statusTlo = 'var(--warning-light)';
                    statusKolor = 'var(--warning)';
                    statusTekst = 'Skonfigurowane, wylaczone';
                    StatusIkona = FaExclamationTriangle;
                  }

                  return (
                    <tr key={loc.id}>
                      <td style={{ ...komorka, fontWeight: '600' }}>{nazwa}</td>
                      <td style={{ ...komorka, color: maAdres ? 'var(--text)' : 'var(--text-muted)' }}>
                        {maAdres ? cfg.from_email : '—'}
                      </td>
                      <td style={{ ...komorka, color: 'var(--text-secondary)', fontSize: '11px' }}>
                        {maAdres ? (cfg.transport === 'gmail_oauth' ? 'Gmail (OAuth)' : 'SMTP') : '—'}
                      </td>
                      <td style={komorka}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                          backgroundColor: statusTlo, color: statusKolor
                        }}>
                          <StatusIkona style={{ fontSize: '9px' }} />
                          {statusTekst}
                        </span>
                      </td>
                      <td style={{ ...komorka, textAlign: 'right' }}>
                        <button
                          onClick={() => setModalFor({ id: loc.id, nazwa })}
                          style={{
                            padding: '4px 10px', fontSize: '11px', fontWeight: '500', cursor: 'pointer',
                            borderRadius: '4px', border: `1px solid ${maAdres ? 'var(--border)' : 'var(--info)'}`,
                            backgroundColor: 'var(--card-bg)',
                            color: maAdres ? 'var(--text-secondary)' : 'var(--info)'
                          }}
                        >
                          {maAdres ? 'Edytuj' : 'Skonfiguruj'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Podpowiedz dla Gmaila - haslo aplikacji, nie haslo do konta */}
      <div style={{
        marginTop: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '4px',
        backgroundColor: 'var(--info-light)', color: 'var(--text-secondary)', fontSize: '11px'
      }}>
        <strong style={{ color: 'var(--info)' }}>Gmail:</strong> w polu hasla wpisz
        {' '}<strong>haslo aplikacji</strong> (Google Account &rarr; Bezpieczenstwo &rarr; Hasla do aplikacji),
        a nie zwykle haslo do konta. Serwer: <code>smtp.gmail.com</code>, port <code>587</code>.
        Haslo jest zapisywane w postaci zaszyfrowanej i nigdy nie wraca do przegladarki.
      </div>

      {/* Slownik adresatow - pracownik wybiera z niego przy wysylce */}
      <AdresaciSekcja />

      <EmailConfigModal
        isOpen={!!modalFor}
        onClose={() => setModalFor(null)}
        locationId={modalFor ? modalFor.id : null}
        locationName={modalFor ? modalFor.nazwa : ''}
        onSaved={() => { setModalFor(null); loadAll(); }}
      />
    </div>
  );
};

export default ReklamacjeEmailTab;
